// Cartao de campo com switch "Sobrescrever" — Base do produto (W5, plano
// secao 5 W5 + README "Herança de override por canal").
//
// Decisao de design registrada (ponto delicado do plano): quando o switch
// esta DESLIGADO, o campo edita o valor BASE (compartilhado por todos os
// canais) — nao fica travado/read-only. Isso e o que faz o Check 0
// sobreviver: a "Base do produto" tem que continuar sendo o lugar onde se
// escreve nome/descricao pela primeira vez (entryMutation, RN10). Quando o
// switch e LIGADO, o campo passa a editar o OVERRIDE deste canal, com
// preview do valor base e "Voltar ao valor base" (desliga o switch, que ao
// salvar manda null — RN10: chave ausente preserva, null limpa).
import type { ReactNode } from "react";
import { Textarea, Input } from "../ui/input";
import { Label } from "../ui/label";
import { cn } from "../../lib/utils";

interface OverrideFieldCardProps {
  id: string;
  label: string;
  type: "text" | "textarea";
  rows?: number;
  baseValue: string;
  overrideActive: boolean;
  overrideValue: string;
  onToggleOverride: (on: boolean) => void;
  onChangeBase: (v: string) => void;
  onChangeOverride: (v: string) => void;
  channelSlug: string;
  disabled?: boolean;
  actions?: ReactNode;
  hint?: ReactNode;
}

export function OverrideFieldCard({
  id,
  label,
  type,
  rows = 3,
  baseValue,
  overrideActive,
  overrideValue,
  onToggleOverride,
  onChangeBase,
  onChangeOverride,
  channelSlug,
  disabled,
  actions,
  hint,
}: OverrideFieldCardProps): JSX.Element {
  return (
    <div className={cn("override", overrideActive && "override--on")}>
      <div className="cluster" style={{ marginBottom: ".5rem" }}>
        <Label htmlFor={id}>{label}</Label>
        <span className="chip" style={overrideActive ? { background: "var(--color-coral-soft)", color: "var(--color-coral)" } : undefined}>
          {overrideActive ? `override ${channelSlug}` : "base"}
        </span>
        {actions}
        {!disabled ? (
          <>
            <button
              type="button"
              role="switch"
              aria-checked={overrideActive}
              aria-label={`Sobrescrever "${label}" no canal ${channelSlug}`}
              title={`Sobrescrever este campo no canal ${channelSlug}`}
              onClick={() => onToggleOverride(!overrideActive)}
              className="switch"
              style={{ marginLeft: "auto", background: overrideActive ? "var(--color-coral)" : "var(--color-line)" }}
            />
            <span style={{ fontSize: "var(--text-sm)", fontWeight: 700, color: "var(--color-ink-2)" }}>Sobrescrever</span>
          </>
        ) : null}
      </div>

      {type === "textarea" ? (
        <Textarea
          id={id}
          value={overrideActive ? overrideValue : baseValue}
          onChange={(e) => (overrideActive ? onChangeOverride(e.target.value) : onChangeBase(e.target.value))}
          disabled={disabled}
          rows={rows}
        />
      ) : (
        <Input
          id={id}
          value={overrideActive ? overrideValue : baseValue}
          onChange={(e) => (overrideActive ? onChangeOverride(e.target.value) : onChangeBase(e.target.value))}
          disabled={disabled}
        />
      )}

      {hint}

      {overrideActive ? (
        <div className="cluster" style={{ marginTop: ".5rem", fontSize: "var(--text-sm)", color: "var(--color-ink-2)" }}>
          <span className="clamp-2 grow" style={{ background: "var(--color-soft)", borderRadius: "var(--radius-sm)", padding: ".25rem .5rem" }}>
            Base: {baseValue || "—"}
          </span>
          {!disabled ? (
            <button
              type="button"
              onClick={() => onToggleOverride(false)}
              style={{ border: 0, background: "transparent", color: "var(--color-coral)", fontWeight: 800, cursor: "pointer" }}
            >
              Voltar ao valor base
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
